import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Button,
  Tag,
  Tooltip,
  Space,
  Typography,
  Spin,
  Alert,
  Rate,
  Modal,
  Descriptions,
  DatePicker
} from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  EyeOutlined,
  FilterOutlined,
  StarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';

const { Title } = Typography;
const { Option } = Select;

interface FeedbackItem {
  id: string;
  ticket_id: string;
  rating: number;
  comment: string;
  is_submitted: boolean;
  submitted_at: string;
  created_at: string;
  ticket_number: string;
  ticket_subject: string;
  assignee_first_name: string;
  assignee_last_name: string;
  business_name: string;
  contact_person: string;
  business_email: string;
  archived_at?: string;
  is_archived?: boolean;
}

const FeedbackDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    is_submitted: undefined as boolean | undefined,
    rating: undefined as number | undefined,
    assignee_id: undefined as string | undefined
  });
  const [ratingFilter, setRatingFilter] = useState<'positive' | 'negative' | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  // Fetch feedback data
  const { data: feedbackData, isLoading, error, refetch } = useQuery(
    ['feedback', filters, dateRange],
    async () => {
      const params = new URLSearchParams();
      if (filters.is_submitted !== undefined) {
        params.append('is_submitted', filters.is_submitted.toString());
      }
      if (filters.rating !== undefined) {
        params.append('rating', filters.rating.toString());
      }
      if (filters.assignee_id) {
        params.append('assignee_id', filters.assignee_id);
      }
      // Add date range to API call - this will include archived feedback
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.append('start_date', dateRange[0].format('YYYY-MM-DD'));
        params.append('end_date', dateRange[1].format('YYYY-MM-DD'));
      }
      
      const response = await api.get(`/api/feedback?${params.toString()}`);
      return response.data;
    }
  );

  // Fetch agents for assignee filter
  const { data: agentsData } = useQuery(
    ['agents'],
    async () => {
      const response = await api.get('/api/users/agents');
      return response.data;
    }
  );

  // Auto-refresh every 10 seconds for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 10000);
    return () => clearInterval(interval);
  }, [refetch]);

  // Fetch feedback statistics
  const { data: statsData } = useQuery(
    ['feedback-stats'],
    async () => {
      const response = await api.get('/api/feedback/stats');
      return response.data;
    }
  );

  // Fetch monthly feedback statistics
  const { data: monthlyStatsData } = useQuery(
    ['feedback-monthly-stats'],
    async () => {
      const response = await api.get('/api/feedback/stats/monthly');
      return response.data;
    }
  );

  const allFeedback = feedbackData?.items || [];
  
  // Apply filters (date filtering is now done on backend)
  let filteredFeedback = allFeedback;
  
  // Apply rating filter (positive = 4-5, negative = 1-2)
  if (ratingFilter === 'positive') {
    filteredFeedback = filteredFeedback.filter((item: FeedbackItem) => 
      item.is_submitted && item.rating && item.rating >= 4 && item.rating <= 5
    );
  } else if (ratingFilter === 'negative') {
    filteredFeedback = filteredFeedback.filter((item: FeedbackItem) => 
      item.is_submitted && item.rating && item.rating >= 1 && item.rating <= 2
    );
  }
  
  const feedback = filteredFeedback;
  
  const stats = statsData;
  const agents = agentsData?.users || [];

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return 'success';
    if (rating >= 3) return 'warning';
    return 'error';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleTicketClick = (ticketId: string) => {
    navigate(`/workspace/${ticketId}`);
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description="Failed to load feedback data. Please try again."
        type="error"
        showIcon
      />
    );
  }

  const monthlyStats = monthlyStatsData;
  const currentMonth = monthlyStats?.current_month;
  const lastMonth = monthlyStats?.last_month;

  // Calculate percentage changes
  const calculateChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const formatMonthName = (monthStr: string) => {
    const date = new Date(monthStr);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <div style={{ marginLeft: '24px', marginRight: '24px' }}>
      <Title level={2}>Customer Feedback Dashboard</Title>

      {/* Monthly Statistics Comparison */}
      {monthlyStats && (
        <Card 
          title="Monthly Statistics Comparison" 
          style={{ marginBottom: '24px' }}
        >
          <Row gutter={[24, 16]}>
            {/* Current Month Column */}
            <Col xs={24} md={12}>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f0f9ff', 
                border: '2px solid #1890ff',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 'bold', 
                  color: '#1890ff',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  📅 {formatMonthName(currentMonth?.month || '')} (This Month)
                </div>
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Total Feedback"
                        value={currentMonth?.total_feedback || 0}
                        prefix={<StarOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Average Rating"
                        value={currentMonth?.average_rating ? Number(currentMonth.average_rating).toFixed(1) : '0.0'}
                        suffix="/ 5"
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Positive (4-5 stars)"
                        value={currentMonth?.positive_feedback || 0}
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Negative (1-2 stars)"
                        value={currentMonth?.negative_feedback || 0}
                        valueStyle={{ color: '#ff4d4f' }}
                      />
                    </Card>
                  </Col>
                </Row>
              </div>
            </Col>

            {/* Last Month Column */}
            <Col xs={24} md={12}>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#fafafa', 
                border: '2px solid #d9d9d9',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 'bold', 
                  color: '#595959',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  📅 {formatMonthName(lastMonth?.month || '')} (Last Month)
                </div>
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Total Feedback"
                        value={lastMonth?.total_feedback || 0}
                        prefix={<StarOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Average Rating"
                        value={lastMonth?.average_rating ? Number(lastMonth.average_rating).toFixed(1) : '0.0'}
                        suffix="/ 5"
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Positive (4-5 stars)"
                        value={lastMonth?.positive_feedback || 0}
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Negative (1-2 stars)"
                        value={lastMonth?.negative_feedback || 0}
                        valueStyle={{ color: '#ff4d4f' }}
                      />
                    </Card>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Feedback"
                value={stats.total_feedback || 0}
                prefix={<StarOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Average Rating"
                value={stats.average_rating ? Number(stats.average_rating).toFixed(1) : '0.0'}
                suffix="/ 5"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card 
              style={{ cursor: 'pointer', transition: 'all 0.3s' }}
              hoverable
              onClick={() => {
                setRatingFilter(ratingFilter === 'positive' ? null : 'positive');
                setFilters(prev => ({ ...prev, rating: undefined }));
              }}
              bodyStyle={{ 
                backgroundColor: ratingFilter === 'positive' ? '#f6ffed' : undefined,
                border: ratingFilter === 'positive' ? '2px solid #52c41a' : undefined
              }}
            >
              <Statistic
                title="Positive (4-5 stars)"
                value={stats.positive_feedback || 0}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card 
              style={{ cursor: 'pointer', transition: 'all 0.3s' }}
              hoverable
              onClick={() => {
                setRatingFilter(ratingFilter === 'negative' ? null : 'negative');
                setFilters(prev => ({ ...prev, rating: undefined }));
              }}
              bodyStyle={{ 
                backgroundColor: ratingFilter === 'negative' ? '#fff1f0' : undefined,
                border: ratingFilter === 'negative' ? '2px solid #ff4d4f' : undefined
              }}
            >
              <Statistic
                title="Negative (1-2 stars)"
                value={stats.negative_feedback || 0}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: '16px' }}>
        <Space wrap>
          <FilterOutlined />
          <DatePicker.RangePicker
            placeholder={['Start Date', 'End Date']}
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null] | null)}
            allowClear
            style={{ width: 250 }}
          />
          <Select
            placeholder="Status"
            style={{ width: 150 }}
            value={filters.is_submitted === undefined ? undefined : filters.is_submitted}
            onChange={(value) => setFilters(prev => ({ 
              ...prev, 
              is_submitted: value === undefined ? undefined : value === true
            }))}
            allowClear
          >
            <Option value={true}>Submitted</Option>
            <Option value={false}>Pending</Option>
          </Select>
          
          <Select
            placeholder="Rating"
            style={{ width: 150 }}
            value={filters.rating}
            onChange={(value) => {
              setFilters(prev => ({ 
                ...prev, 
                rating: value
              }));
              setRatingFilter(null); // Clear positive/negative filter when selecting specific rating
            }}
            allowClear
          >
            <Option value={5}>5 Stars</Option>
            <Option value={4}>4 Stars</Option>
            <Option value={3}>3 Stars</Option>
            <Option value={2}>2 Stars</Option>
            <Option value={1}>1 Star</Option>
          </Select>

          <Select
            placeholder="Assignee"
            style={{ width: 200 }}
            value={filters.assignee_id}
            onChange={(value) => setFilters(prev => ({ 
              ...prev, 
              assignee_id: value
            }))}
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {agents.map((agent: any) => (
              <Option key={agent.id} value={agent.id}>
                {agent.first_name} {agent.last_name}
              </Option>
            ))}
          </Select>
        </Space>
      </Card>

      {/* Feedback Table */}
      <Table
        dataSource={feedback}
        rowKey="id"
        columns={[
          {
            title: 'Store',
            dataIndex: 'business_name',
            key: 'store',
            render: (text: string, record: FeedbackItem) => (
              <div>
                <div style={{ fontWeight: 500 }}>{text}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {record.contact_person}
                </div>
              </div>
            ),
          },
          {
            title: 'Ticket',
            dataIndex: 'ticket_number',
            key: 'ticket',
            render: (text: string, record: FeedbackItem) => (
              <div>
                <a 
                  onClick={() => handleTicketClick(record.ticket_id)}
                  style={{ cursor: 'pointer', color: '#1890ff' }}
                >
                  #{text}
                </a>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {record.ticket_subject}
                </div>
              </div>
            ),
          },
          {
            title: 'Assignee',
            key: 'assignee',
            render: (_, record: FeedbackItem) => 
              record.assignee_first_name && record.assignee_last_name 
                ? `${record.assignee_first_name} ${record.assignee_last_name}`
                : 'Unassigned',
          },
          {
            title: 'Rating',
            dataIndex: 'rating',
            key: 'rating',
            render: (rating: number, record: FeedbackItem) => 
              record.is_submitted ? (
                <Space>
                  <Rate disabled value={rating} />
                  <Tag color={getRatingColor(rating)}>{rating}</Tag>
                </Space>
              ) : (
                <span style={{ color: '#999' }}>Not rated</span>
              ),
          },
          {
            title: 'Comment',
            dataIndex: 'comment',
            key: 'comment',
            render: (comment: string) => 
              comment ? (
                <div style={{ maxWidth: 200 }}>
                  {comment.length > 100 ? `${comment.substring(0, 100)}...` : comment}
                </div>
              ) : (
                <span style={{ color: '#999' }}>No comment</span>
              ),
          },
          {
            title: 'Date',
            dataIndex: 'submitted_at',
            key: 'date',
            render: (date: string, record: FeedbackItem) => 
              formatDate(date || record.created_at),
          },
              {
                title: 'Status',
                dataIndex: 'is_submitted',
                key: 'status',
                render: (isSubmitted: boolean, record: FeedbackItem) => (
                  <Space>
                    <Tag color={isSubmitted ? 'success' : 'warning'}>
                      {isSubmitted ? 'Submitted' : 'Pending'}
                    </Tag>
                    {record.is_archived && (
                      <Tag color="default">Archived</Tag>
                    )}
                  </Space>
                ),
              },
          {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: FeedbackItem) => (
              <Tooltip title="View Details">
                <Button 
                  type="text" 
                  icon={<EyeOutlined />} 
                  size="small" 
                  onClick={() => {
                    setSelectedFeedback(record);
                    setModalVisible(true);
                  }}
                />
              </Tooltip>
            ),
          },
        ]}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        locale={{
          emptyText: 'No feedback found matching your filters.',
        }}
      />

      {/* Feedback Details Modal */}
      <Modal
        title="Feedback Details"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedFeedback(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setModalVisible(false);
            setSelectedFeedback(null);
          }}>
            Close
          </Button>,
          selectedFeedback && (
            <Button 
              key="ticket" 
              type="primary" 
              onClick={() => {
                navigate(`/workspace/${selectedFeedback.ticket_id}`);
                setModalVisible(false);
                setSelectedFeedback(null);
              }}
            >
              View Ticket
            </Button>
          ),
        ]}
        width={700}
      >
        {selectedFeedback && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Ticket">
              <Space>
                <a 
                  onClick={() => {
                    navigate(`/workspace/${selectedFeedback.ticket_id}`);
                    setModalVisible(false);
                    setSelectedFeedback(null);
                  }}
                  style={{ cursor: 'pointer', color: '#1890ff' }}
                >
                  #{selectedFeedback.ticket_number}
                </a>
                <span style={{ color: '#666' }}>{selectedFeedback.ticket_subject}</span>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Business">
              <div>
                <div style={{ fontWeight: 500 }}>{selectedFeedback.business_name}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {selectedFeedback.contact_person}
                </div>
                {selectedFeedback.business_email && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {selectedFeedback.business_email}
                  </div>
                )}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Assignee">
              {selectedFeedback.assignee_first_name && selectedFeedback.assignee_last_name
                ? `${selectedFeedback.assignee_first_name} ${selectedFeedback.assignee_last_name}`
                : 'Unassigned'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Space>
                <Tag color={selectedFeedback.is_submitted ? 'success' : 'warning'}>
                  {selectedFeedback.is_submitted ? 'Submitted' : 'Pending'}
                </Tag>
                {selectedFeedback.is_archived && (
                  <Tag color="default">Archived</Tag>
                )}
              </Space>
            </Descriptions.Item>
            {selectedFeedback.is_submitted && (
              <>
                <Descriptions.Item label="Rating">
                  <Space>
                    <Rate disabled value={selectedFeedback.rating || 0} />
                    <Tag color={getRatingColor(selectedFeedback.rating || 0)}>
                      {selectedFeedback.rating} / 5
                    </Tag>
                  </Space>
                </Descriptions.Item>
                {selectedFeedback.comment && (
                  <Descriptions.Item label="Comment">
                    <div style={{ whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>
                      {selectedFeedback.comment}
                    </div>
                  </Descriptions.Item>
                )}
              </>
            )}
            <Descriptions.Item label="Feedback Request Sent">
              {formatDate(selectedFeedback.created_at)}
            </Descriptions.Item>
            {selectedFeedback.submitted_at && (
              <Descriptions.Item label="Feedback Submitted">
                {formatDate(selectedFeedback.submitted_at)}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default FeedbackDashboard;
